import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import { buildTemplateEditorExtensions, stripEditorTableNoise } from './tiptapScribanExtensions';
import { ScribanPaletteContext } from './scribanPaletteContext';
import { scopeTemplateStyles } from './scribanHtmlTransform';

/** Debounce for pushing edits up to the parent (which feeds the 800 ms preview debounce). */
const VISUAL_DIRTY_DEBOUNCE_MS = 400;

export interface VisualEditorHandle {
  /** Current editor content as marker HTML — immediate, for mode switches and save. */
  getBodyHtml(): string;
  /** Inserts a placeholder chip at the current selection. */
  insertExpression(expr: string): void;
}

interface DocumentTemplateVisualEditorProps {
  /** Marker HTML from parseTemplate — the component is mounted fresh per visual-mode entry. */
  initialBodyHtml: string;
  /** Raw template CSS; scoped to this pane so the WYSIWYG looks like the document. */
  styleText: string;
  /** Expression paths for the chip popover autocomplete. */
  paletteOptions: string[];
  /**
   * Called synchronously on the first real document change — the parent must know the editor
   * is dirty even if a mode switch happens before the debounced onDirtyChange fires.
   */
  onDirty: () => void;
  /** Debounced on every real edit with the current marker HTML. */
  onDirtyChange: (bodyHtml: string) => void;
}

export const DocumentTemplateVisualEditor = forwardRef<VisualEditorHandle, DocumentTemplateVisualEditorProps>(
  function DocumentTemplateVisualEditor({ initialBodyHtml, styleText, paletteOptions, onDirty, onDirtyChange }, ref) {
    const debounceRef = useRef<number>(undefined);
    const onDirtyRef = useRef(onDirty);
    onDirtyRef.current = onDirty;
    const onDirtyChangeRef = useRef(onDirtyChange);
    onDirtyChangeRef.current = onDirtyChange;

    const editor = useEditor({
      extensions: buildTemplateEditorExtensions(),
      content: initialBodyHtml,
      onUpdate: ({ editor: current }) => {
        onDirtyRef.current();
        window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(
          () => onDirtyChangeRef.current(stripEditorTableNoise(current.getHTML())),
          VISUAL_DIRTY_DEBOUNCE_MS,
        );
      },
    });

    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    useImperativeHandle(
      ref,
      () => ({
        getBodyHtml: () => (editor ? stripEditorTableNoise(editor.getHTML()) : initialBodyHtml),
        insertExpression: (expr: string) => {
          editor?.chain().focus().insertContent({ type: 'scribanExpr', attrs: { expr } }).run();
        },
      }),
      [editor, initialBodyHtml],
    );

    const scopedCss = useMemo(() => scopeTemplateStyles(styleText, 'scriban-wysiwyg'), [styleText]);

    return (
      <ScribanPaletteContext.Provider value={paletteOptions}>
        <Box
          className="scriban-wysiwyg"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            flexShrink: 0,
            p: 1.5,
            bgcolor: 'background.paper',
            '& .tiptap': { outline: 'none', minHeight: 400 },
            // Loop-marked elements repeat per item at render time — make them visually distinct.
            '& [data-scriban-for]': {
              outline: '2px dashed',
              outlineColor: 'secondary.main',
              outlineOffset: '-1px',
            },
          }}
        >
          {scopedCss && <style>{scopedCss}</style>}
          <EditorContent editor={editor} />
        </Box>
      </ScribanPaletteContext.Provider>
    );
  },
);
